/**
 * Copyright IBM Corp. 2022, 2026
 */

import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@hashicorp/mds-react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import ReactPlayer from 'react-player'
import WistiaPlayer from 'react-player/wistia'
import classNames from 'classnames'
import s from './style.module.css'

interface VideoPlayerModalProps {
	onDismiss: () => void
	videoUrl: string
	label: string
	appearance?: 'light' | 'dark'
}

const VideoPlayerModal = ({
	onDismiss,
	videoUrl,
	label,
	appearance = 'light',
}: VideoPlayerModalProps) => {
	const isWistiaVideo = videoUrl.includes('wistia')
	const ariaLabel = `${label} video`
	return (
		<Dialog.Root
			open
			onOpenChange={(isOpen) => {
				!isOpen && onDismiss()
			}}
		>
			<Dialog.Portal>
				<Dialog.Overlay
					className={classNames([s.dialogOverlay, s[appearance]])}
				>
					<div className={s.closeIconBttnWrapper}>
						<Button
							onClick={onDismiss}
							icon="x"
							color="secondary"
							aria-label={'Close video'}
						/>
					</div>
				</Dialog.Overlay>
				<Dialog.Content className={s.dialogContent}>
					<VisuallyHidden asChild>{ariaLabel}</VisuallyHidden>
					<div className={s.playerWrapper}>
						{isWistiaVideo ? (
							<WistiaPlayer
								className={s.player}
								url={videoUrl}
								width="100%"
								height="100%"
								controls
								playsInline
								playing
								style={{
									borderRadius: 8,
									overflow: 'hidden',
								}}
								config={{
									options: {
										controlsVisibleOnLoad: false,
									},
								}}
							/>
						) : (
							<ReactPlayer
								className={s.player}
								url={videoUrl}
								width="100%"
								height="100%"
								controls
								playsInline
								playing
								style={{
									borderRadius: 8,
									overflow: 'hidden',
								}}
							/>
						)}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}

export default VideoPlayerModal
